/* eslint-disable react/no-unstable-nested-components --
 * O Tiptap usa render-props para toolbar buttons — falso positivo conhecido.
 * Justificativa em /app/frontend/eslint.config.mjs.
 */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect } from "react";
import {
  Bold, Italic, Strikethrough, Underline as UnderlineIcon, Highlighter,
  List, ListOrdered, ListChecks, Quote, Code, Link as LinkIcon,
  Heading1, Heading2, Heading3, Minus,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Undo2, Redo2,
} from "lucide-react";

/**
 * Reusable Tiptap editor for rich text comments.
 * - Controlled via `value` (HTML string) and `onChange`.
 * - Styled with Tailwind; toolbar is sticky inside the bordered container.
 */
export default function TiptapEditor({
  value, onChange, placeholder = "Escreva um comentário...",
  minHeight = 100, testIdPrefix = "tiptap", autoFocus = false, fill = false,
}) {
  const editorClass = [
    "max-w-none focus:outline-none px-3 py-2 text-[13px] leading-[1.5] text-gray-800",
    "[&_p]:my-1",
    "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1",
    "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1",
    "[&_li]:my-0.5 [&_li]:marker:text-gray-400",
    // Task list (Tiptap) — remove disc/decimal and align checkbox with text
    "[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-0 [&_ul[data-type=taskList]]:my-1",
    "[&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:gap-2 [&_ul[data-type=taskList]_li]:items-start",
    "[&_ul[data-type=taskList]_li_label]:mt-0.5 [&_ul[data-type=taskList]_li_label]:shrink-0",
    "[&_ul[data-type=taskList]_li_div]:flex-1 [&_ul[data-type=taskList]_li_div_p]:my-0",
    "[&_ul[data-type=taskList]_li[data-checked=true]_div]:line-through [&_ul[data-type=taskList]_li[data-checked=true]_div]:text-gray-400",
    "[&_h1]:text-[15px] [&_h1]:font-semibold [&_h1]:my-1.5",
    "[&_h2]:text-[14px] [&_h2]:font-semibold [&_h2]:my-1.5",
    "[&_h3]:text-[13px] [&_h3]:font-semibold [&_h3]:my-1",
    "[&_strong]:font-semibold",
    "[&_em]:italic",
    "[&_u]:underline",
    "[&_mark]:bg-yellow-200 [&_mark]:text-gray-900 [&_mark]:px-0.5 [&_mark]:rounded-sm",
    "[&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:my-1",
    "[&_code]:bg-gray-100 [&_code]:text-rose-600 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[11.5px] [&_code]:font-mono",
    "[&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:p-2.5 [&_pre]:rounded-md [&_pre]:text-[11.5px] [&_pre]:my-1.5 [&_pre]:overflow-x-auto",
    "[&_pre_code]:bg-transparent [&_pre_code]:text-gray-100 [&_pre_code]:p-0",
    "[&_a]:text-blue-600 [&_a]:underline",
    "[&_hr]:my-2 [&_hr]:border-gray-200",
  ].join(" ");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Highlight.configure({ multicolor: false }),
      TaskList,
      TaskItem.configure({ nested: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
    ],
    content: value || "",
    autofocus: autoFocus,
    editorProps: {
      attributes: {
        class: editorClass,
        style: `min-height:${minHeight}px;`,
        "data-testid": `${testIdPrefix}-content`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const isEmpty = ed.isEmpty;
      onChange(isEmpty ? "" : html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (current !== incoming && !(editor.isEmpty && incoming === "")) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const Btn = ({ onClick, active, label, children, testId }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={`h-7 w-7 inline-flex items-center justify-center rounded transition-colors ${
        active ? "bg-gray-200 text-gray-900" : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
      }`}
    >
      {children}
    </button>
  );

  const Sep = () => <span className="mx-0.5 h-4 w-px bg-gray-200" />;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
     
    const url = window.prompt("URL do link (deixe em branco para remover):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  return (
    <div className={`rounded-lg border border-gray-200 bg-white focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-300 transition-colors ${fill ? "flex flex-col h-full min-h-0" : ""}`} data-testid={`${testIdPrefix}-wrapper`}>
      <div className="flex items-center gap-0 flex-wrap px-1.5 py-1 border-b border-gray-100 bg-gray-50/60 rounded-t-lg">
        {/* Text styles */}
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="Negrito (Ctrl+B)" testId={`${testIdPrefix}-bold`}><Bold size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="Itálico (Ctrl+I)" testId={`${testIdPrefix}-italic`}><Italic size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} label="Sublinhado (Ctrl+U)" testId={`${testIdPrefix}-underline`}><UnderlineIcon size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} label="Tachado" testId={`${testIdPrefix}-strike`}><Strikethrough size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive("highlight")} label="Marca-texto" testId={`${testIdPrefix}-highlight`}><Highlighter size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} label="Código inline" testId={`${testIdPrefix}-code`}><Code size={13} /></Btn>
        <Sep />
        {/* Headings */}
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} label="Título 1" testId={`${testIdPrefix}-h1`}><Heading1 size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Título 2" testId={`${testIdPrefix}-h2`}><Heading2 size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Título 3" testId={`${testIdPrefix}-h3`}><Heading3 size={13} /></Btn>
        <Sep />
        {/* Lists */}
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Lista" testId={`${testIdPrefix}-bullet`}><List size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Lista numerada" testId={`${testIdPrefix}-ordered`}><ListOrdered size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive("taskList")} label="Checklist" testId={`${testIdPrefix}-tasklist`}><ListChecks size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Citação" testId={`${testIdPrefix}-quote`}><Quote size={13} /></Btn>
        <Sep />
        {/* Align */}
        <Btn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} label="Alinhar à esquerda" testId={`${testIdPrefix}-align-left`}><AlignLeft size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} label="Centralizar" testId={`${testIdPrefix}-align-center`}><AlignCenter size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} label="Alinhar à direita" testId={`${testIdPrefix}-align-right`}><AlignRight size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} label="Justificar" testId={`${testIdPrefix}-align-justify`}><AlignJustify size={13} /></Btn>
        <Sep />
        {/* Misc */}
        <Btn onClick={promptLink} active={editor.isActive("link")} label="Link" testId={`${testIdPrefix}-link`}><LinkIcon size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} label="Linha divisória" testId={`${testIdPrefix}-hr`}><Minus size={13} /></Btn>
        <span className="flex-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()} label="Desfazer" testId={`${testIdPrefix}-undo`}><Undo2 size={13} /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} label="Refazer" testId={`${testIdPrefix}-redo`}><Redo2 size={13} /></Btn>
      </div>
      <EditorContent
        editor={editor}
        className={fill ? "flex-1 min-h-0 overflow-y-auto [&>.ProseMirror]:h-full" : undefined}
      />
    </div>
  );
}
